'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { apiFetch, API_BASE } from './api';

// Next.js 16 / React 19 false positive: "Encountered a script tag" warning
// from next/script. The scripts work correctly — this just silences the noise.
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  const origErr = console.error;
  console.error = (...args: unknown[]) => {
    if (typeof args[0] === 'string' && args[0].includes('Encountered a script tag')) return;
    origErr.apply(console, args);
  };
}

type Theme = 'light' | 'dark';

type ThemeContextType = {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function applyTheme(newTheme: Theme) {
  if (newTheme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Initialize from localStorage synchronously to match the inline script in layout.tsx.
  // This prevents React hydration mismatch where theme='light' default would cause
  // a brief flash before useEffect runs.
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'light';
    const stored = window.localStorage.getItem('theme') as Theme;
    return stored === 'dark' || stored === 'light' ? stored : 'light';
  });

  // Step 1: Ensure DOM matches state (inline script already did this, but be safe)
  useEffect(() => {
    applyTheme(theme);
    // Enable transitions after initial theme is applied
    requestAnimationFrame(() => {
      document.documentElement.classList.add('theme-ready');
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 2: If no user preference in localStorage, try user profile from DB, then admin public settings
  useEffect(() => {
    const stored = window.localStorage.getItem('theme');
    if (stored === 'dark' || stored === 'light') return; // User has own preference

    const email = window.localStorage.getItem('email');
    if (email) {
      // Try user profile from DB
      apiFetch<{ themePreference?: string }>('/api/auth/me', { silent: true })
        .then((profile) => {
          if (profile?.themePreference === 'light' || profile?.themePreference === 'dark') {
            const t = profile.themePreference as Theme;
            setThemeState(t);
            applyTheme(t);
            window.localStorage.setItem('theme', t); // cache for next load
          } else {
            fetchAdminDefault();
          }
        })
        .catch(() => fetchAdminDefault());
    } else {
      fetchAdminDefault();
    }

    function fetchAdminDefault() {
      fetch(`${API_BASE}/api/settings/public`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (!data?.color_scheme) return;
          if (data.color_scheme === 'light') {
            setThemeState('light');
            applyTheme('light');
            window.localStorage.setItem('theme', 'light');
          } else if (data.color_scheme === 'dark') {
            setThemeState('dark');
            applyTheme('dark');
            window.localStorage.setItem('theme', 'dark');
          }
        })
        .catch((e) => console.error("[api]", e));
    }
  }, []);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    window.localStorage.setItem('theme', newTheme);
    applyTheme(newTheme);

    // Persist to DB if logged in
    const email = window.localStorage.getItem('email');
    if (email) {
      apiFetch('/api/auth/theme', {
        method: 'POST',
        body: JSON.stringify({ theme: newTheme }),
      }).catch((e) => console.error("[api]", e));
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
